import { useState } from "react";
import { Bell, ExternalLink } from "lucide-react";
import { subscribeEmail } from "@/lib/newsletter.functions";
import { track } from "@/lib/analytics";
import { formatReleaseDate, type StreamingPlatform } from "@/lib/content";
import { trackMarketingPresaveClick, trackMarketingSignupSuccess } from "@/lib/marketing/analytics";

interface Props {
  slug: string;
  title: string;
  releaseDate?: string;
  /** Primary distributor pre-save destination (DistroKid HyperFollow). */
  preSaveUrl?: string;
  /** Additional per-platform pre-save destinations. Rendered only when real URLs exist. */
  preSaveLinks?: Partial<Record<StreamingPlatform, string>>;
}

/**
 * Pre-release call to action. Distributor pre-save buttons appear first when
 * real URLs are supplied; on-site reminder capture is always available so the
 * funnel never dead-ends before release day.
 */
export function PreSaveCTA({ slug, title, releaseDate, preSaveUrl, preSaveLinks }: Props) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "success" | "duplicate" | "error">("idle");
  const [message, setMessage] = useState("");

  const dateLabel = formatReleaseDate({ releaseDate });
  const source = `presave:${slug}`;
  const links = Object.entries(preSaveLinks ?? {}).filter(([, url]) => Boolean(url)) as [string, string][];

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (state === "loading") return;
    setState("loading");
    setMessage("");
    try {
      const res = await subscribeEmail({ data: { email, source } });
      track("release_presave_submit", { slug, duplicate: Boolean(res.duplicate) });
      trackMarketingSignupSuccess({ source, duplicate: Boolean(res.duplicate) });
      if (res.duplicate) {
        setState("duplicate");
        setMessage("You're already on the list — we'll reach you on release day.");
      } else {
        setState("success");
        setMessage("Confirmed path. Check your inbox to seal the reminder.");
        setEmail("");
      }
    } catch (err) {
      setState("error");
      setMessage(err instanceof Error && err.message ? err.message : "Please enter a valid email.");
    }
  }

  return (
    <section
      aria-labelledby={`presave-${slug}`}
      className="rounded-2xl border border-gold/40 bg-temple p-6 shadow-gold sm:p-8"
    >
      <p className="text-[11px] uppercase tracking-[0.3em] text-gold">Pre-release</p>
      <h2 id={`presave-${slug}`} className="mt-2 font-serif text-2xl text-ivory sm:text-3xl">
        Be there when {title} arrives
      </h2>
      <p className="mt-3 text-sm text-muted-foreground">
        {dateLabel
          ? `Releasing ${dateLabel}. Pre-save it, or leave your address and we will send it the hour it lands.`
          : "Pre-save it, or leave your address and we will send it the hour it lands."}
      </p>

      {preSaveUrl && (
        <a
          href={preSaveUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            track("release_presave_click", { slug, platform: "hyperfollow", placement: "presave_panel" });
            trackMarketingPresaveClick({ slug, platform: "hyperfollow", surface: "presave_panel" });
          }}
          className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-base font-semibold text-obsidian shadow-gold sm:w-auto"
        >
          Pre-save {title} <ExternalLink size={14} aria-hidden />
        </a>
      )}

      {links.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-3">
          {links.map(([platform, url]) => (
            <a
              key={platform}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                track("release_presave_click", { slug, platform });
                trackMarketingPresaveClick({ slug, platform, surface: "presave_panel" });
              }}
              className="inline-flex min-h-11 items-center gap-2 rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian"
            >
              Pre-save on {platform} <ExternalLink size={12} aria-hidden />
            </a>
          ))}
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-2 sm:flex-row" noValidate>
        <label htmlFor={`presave-email-${slug}`} className="sr-only">
          Email for {title} release reminder
        </label>
        <input
          id={`presave-email-${slug}`}
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@sacred.address"
          disabled={state === "loading"}
          aria-invalid={state === "error"}
          aria-describedby={message ? `presave-msg-${slug}` : undefined}
          className="min-h-11 min-w-0 flex-1 rounded-full border hairline bg-obsidian px-4 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-gold disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={state === "loading"}
          className={`inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-full px-5 py-2 text-sm font-semibold disabled:opacity-60 ${
            links.length > 0
              ? "border border-gold text-gold-luminous hover:bg-gold hover:text-obsidian"
              : "bg-gold-gradient text-obsidian"
          }`}
        >
          <Bell size={14} aria-hidden />
          {state === "loading" ? "Sending…" : "Notify me on release day"}
        </button>
      </form>

      {message && (
        <p
          id={`presave-msg-${slug}`}
          role={state === "error" ? "alert" : "status"}
          aria-live="polite"
          className={`mt-3 text-xs ${state === "error" ? "text-red-300" : "text-gold-luminous"}`}
        >
          {message}
        </p>
      )}
    </section>
  );
}
